import { useState, type FormEvent } from 'react'
import { isAxiosError } from 'axios'
import { Field } from '@/components/common/Field'
import { inputClass, primaryButtonClass, selectClass } from '@/components/common/formStyles'
import { useUpdateExternalAsset } from '@/lib/useExternalAssets'
import type { AssetClass, ExternalAsset, UpdateFrequency } from '@/types/externalAsset'

const ASSET_CLASS_LABELS: Record<AssetClass, string> = {
  crypto: 'Crypto (BTC, ETH …)',
  real_estate: 'Immobilie',
  commodity_physical: 'Commodity physisch (Gold/Silber)',
  cash: 'Cash (Tagesgeld/Konto)',
  other: 'Sonstiges',
}

const UPDATE_FREQUENCY_LABELS: Record<UpdateFrequency, string> = {
  monthly: 'Monatlich',
  quarterly: 'Quartalsweise',
  on_demand: 'Bei Bedarf',
}

interface EditExternalAssetFormProps {
  asset: ExternalAsset
  onSuccess?: (asset: ExternalAsset) => void
}

export function EditExternalAssetForm({ asset, onSuccess }: EditExternalAssetFormProps) {
  const updateAsset = useUpdateExternalAsset()

  const [name, setName] = useState(asset.name)
  const [assetClass, setAssetClass] = useState<AssetClass>(asset.asset_class)
  const [valueEur, setValueEur] = useState(asset.current_value_eur)
  const [updateFrequency, setUpdateFrequency] = useState<UpdateFrequency>(asset.update_frequency)
  const [source, setSource] = useState(asset.source ?? '')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmedValue = valueEur.trim()
    if (!trimmedValue) {
      setError('EUR-Wert eingeben.')
      return
    }
    updateAsset.mutate(
      {
        id: asset.id,
        body: {
          name: name.trim(),
          asset_class: assetClass,
          current_value_eur: trimmedValue,
          update_frequency: updateFrequency,
          source: source.trim() || null,
        },
      },
      {
        onSuccess: (updated) => {
          onSuccess?.(updated)
        },
        onError: (err) => {
          if (isAxiosError(err) && err.response?.status === 422) {
            setError('Eingaben prüfen (EUR-Wert ≥ 0, Asset-Class gültig).')
          } else if (isAxiosError(err) && err.response?.status === 404) {
            setError('Asset nicht gefunden — vermutlich gelöscht.')
          } else {
            setError('Fehler beim Speichern. Erneut versuchen.')
          }
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={128}
          required
          className={inputClass}
        />
      </Field>

      <Field label="Asset-Class">
        <select
          value={assetClass}
          onChange={(e) => setAssetClass(e.target.value as AssetClass)}
          className={selectClass}
        >
          {Object.entries(ASSET_CLASS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="EUR-Wert (aktuell)">
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={valueEur}
          onChange={(e) => setValueEur(e.target.value)}
          required
          className={`${inputClass} font-mono`}
        />
      </Field>

      <Field label="Update-Frequenz">
        <select
          value={updateFrequency}
          onChange={(e) => setUpdateFrequency(e.target.value as UpdateFrequency)}
          className={selectClass}
        >
          {Object.entries(UPDATE_FREQUENCY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Quelle (optional)">
        <input
          value={source}
          onChange={(e) => setSource(e.target.value)}
          maxLength={64}
          placeholder="z.B. CoinTracking, Notar-Akte"
          className={inputClass}
        />
      </Field>

      {error && (
        <p role="alert" className="font-mono text-xs text-loss">
          {error}
        </p>
      )}

      <button type="submit" disabled={updateAsset.isPending} className={primaryButtonClass}>
        {updateAsset.isPending ? 'Speichert…' : 'Änderungen speichern'}
      </button>
    </form>
  )
}
